import { locationDisplayAddress } from '../../../services/locationAddress'
import {UnionShiftAwards} from '../scheduling/UnionShiftAwards'
import React, { useEffect, useMemo, useState } from 'react'
import {
  apiErrorMessage,
  approveShiftApplication,
  cancelJobShift,
  createJobAssignment,
  createJobShift,
  duplicateJobShift,
  getScheduleShiftDetail,
  listJobs,
  listScheduleCalendarEvents,
  publishJobShift,
  rejectShiftApplication,
  updateJobAssignmentStatus,
  updateJobShift,
} from '../../../services/access'
import type {
  AccessibleLocation,
  Job,
  JobAssignment,
  JobAssignmentStatus,
  JobShiftWorkerMatch,
  JobShiftStatus,
  Organization,
  ScheduleCalendarEvent,
  ScheduleCalendarFilters,
  ScheduleAvailabilityState,
  ScheduleConflictState,
  ScheduleFillState,
  ScheduleShiftConflict,
  ShiftApplication,
  WorkMode,
  WorkerOrgRelationship,
} from '../../../types/access'
import { DetailDrawer, EmptyState, ErrorBanner, FormModal, LoadingState } from './AdminUiPrimitives'
import { ShiftHistoryViewer } from './ShiftHistoryViewer'
import { organizationLabel } from './adminPanelUtils'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { useI18n } from '../../../i18n/I18nContext'
import { GuidedHelp, useAdminExperience } from '../../../context/AdminExperienceContext'

interface ScheduleCalendarViewProps {
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  workerRelationships?: WorkerOrgRelationship[]
  selectedOrgId: string
  canViewSchedule: boolean
  canManageJobs: boolean
  canManageApplications: boolean
  canManageAssignments?: boolean
  canViewPayRate: boolean
  canViewBillRate: boolean
  onOpenJob: (jobId: string) => void
  onOpenWorkerProfile?: (workerProfileId: string) => void
}

type CalendarMode = 'month' | 'week' | 'day'

const statuses: Array<JobShiftStatus | ''> = ['', 'draft', 'published', 'open', 'partially_filled', 'filled', 'in_progress', 'completed', 'cancelled']
const fillStates: Array<ScheduleFillState | ''> = ['', 'empty', 'partial', 'filled', 'overfilled']
const conflictStates: Array<ScheduleConflictState | ''> = ['', 'clear', 'warning', 'conflict']
const availabilityStates: Array<ScheduleAvailabilityState | ''> = ['', 'available', 'unavailable', 'conflicted', 'assigned']
const colorStates = ['draft', 'open', 'partial', 'filled', 'conflict', 'completed', 'cancelled']
const activeAssignmentStatuses = ['accepted', 'confirmed', 'in_progress', 'completed']
const workModes: Array<WorkMode | ''> = ['', 'onsite', 'hybrid', 'remote']

const pad = (value: number) => String(value).padStart(2, '0')
const dateKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const addDays = (date: Date, days: number) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}
const readable = (value?: string | null) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-'
const formatTime = (value: string, timeZone?: string | null) => new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
const formatDateTime = (value: string, timeZone?: string | null) => new Date(value).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
const openPositions = (assigned: number, required: number) => Math.max(Number(required || 0) - Number(assigned || 0), 0)
const coverageLabel = (assigned: number, required: number) => {
  const open = openPositions(assigned, required)
  if (open === 0 && assigned > required) return 'Overfilled'
  if (open === 0) return 'Filled'
  if (assigned > 0) return 'Partial'
  return 'Unfilled'
}
const isNonEmptyString = (value: string | null | undefined): value is string => Boolean(value)
const matchDistanceDiagnostic = (match?: JobShiftWorkerMatch | null) => {
  if (!match) return ''
  const meters = match.distanceMeters ?? (match.distance_km == null ? null : Number(match.distance_km) * 1000)
  const radiusMeters = match.effectiveRadiusMeters ?? (match.effective_radius_km == null ? null : Number(match.effective_radius_km) * 1000)
  const parts = []
  if (meters != null && Number.isFinite(Number(meters))) parts.push(`${(Number(meters) / 1000).toFixed(1)} km away`)
  if (radiusMeters != null && Number.isFinite(Number(radiusMeters))) parts.push(`${(Number(radiusMeters) / 1000).toFixed(1)} km limit`)
  const source = match.locationSource || match.location_source
  if (source) parts.push(`${readable(String(source))} location`)
  if (match.eligibility_reason) parts.push(readable(match.eligibility_reason))
  return parts.join(' / ')
}

const matchMissingRequiredSkills = (match?: JobShiftWorkerMatch | null) => (
  (match?.missingRequiredSkills || match?.missing_skill_names || []).filter(Boolean)
)
const matchBelowRequiredLevelSkills = (match?: JobShiftWorkerMatch | null) => (
  (match?.belowRequiredLevelSkills || match?.below_required_level_skill_names || []).filter(Boolean)
)
const matchCertificatePendingSkills = (match?: JobShiftWorkerMatch | null) => (
  (match?.certificatePendingSkills || match?.certificate_pending_skill_names || []).filter(Boolean)
)
const matchCertificateExpiredSkills = (match?: JobShiftWorkerMatch | null) => (
  (match?.certificateExpiredSkills || match?.certificate_expired_skill_names || []).filter(Boolean)
)
const matchDiagnosticChips = (match: JobShiftWorkerMatch | null | undefined, t: (text: string, vars?: Record<string, string | number>) => string) => {
  if (!match) return []
  const chips = [
    ...matchMissingRequiredSkills(match).map((skill) => ({ key: `missing-${skill}`, label: t('Missing: {skill}', { skill }) })),
    ...matchBelowRequiredLevelSkills(match).map((skill) => ({ key: `below-${skill}`, label: t('Below required level: {skill}', { skill }) })),
    ...matchCertificatePendingSkills(match).map((skill) => ({ key: `pending-${skill}`, label: t('Certificate pending: {skill}', { skill }) })),
    ...matchCertificateExpiredSkills(match).map((skill) => ({ key: `expired-${skill}`, label: t('Certificate expired: {skill}', { skill }) })),
  ]
  ;(match.eligibilityDiagnostics || match.eligibility_diagnostics || []).forEach((diagnostic, index) => {
    const code = diagnostic.diagnostic || 'eligible'
    if (['missing_skill', 'below_required_level', 'certificate_pending', 'certificate_expired'].includes(code)) return
    const name = diagnostic.canonicalName || diagnostic.canonical_name || diagnostic.skillName || diagnostic.skill_name || t('Skill')
    chips.push({ key: `diag-${index}-${code}`, label: `${t(readable(code))}: ${name}` })
  })
  if (!chips.length && match.eligible !== false) chips.push({ key: 'eligible', label: t('Eligible') })
  return chips
}

const rangeFor = (anchor: Date, mode: CalendarMode) => {
  if (mode === 'day') {
    const start = startOfDay(anchor)
    return { start, end: addDays(start, 1), days: [start] }
  }
  if (mode === 'week') {
    const start = startOfDay(anchor)
    start.setDate(start.getDate() - start.getDay())
    const days = Array.from({ length: 7 }, (_, index) => addDays(start, index))
    return { start, end: addDays(start, 7), days }
  }
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  const gridStart = addDays(start, -start.getDay())
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index))
  return { start: gridStart, end: addDays(gridStart, 42), days }
}

const locationLabel = (location: AccessibleLocation, fallbackNoun = 'location') => {
  const address = locationDisplayAddress(location)
  const name = location.name || location.label || location.location_key || `Unnamed ${fallbackNoun}`
  return address ? `${name} - ${address}` : name
}

const workerOptionLabel = (worker: WorkerOrgRelationship, fallbackWorker = 'Worker') => {
  const row = worker as any
  return `${worker.display_name || worker.worker_email || worker.worker_phone || fallbackWorker} / ${worker.worker_email || worker.worker_phone || 'No contact'} / ${row.relationship_type || row.org_name || row.organization_name || `Visible ${fallbackWorker.toLowerCase()}`}`
}

const formatScore = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return ''
  const score = Number(value)
  return Number.isFinite(score) ? `${Math.round(score)}/100` : String(value)
}

const conflictText = (conflict?: ScheduleShiftConflict | null) => conflict?.message || readable(conflict?.conflict_type)

export const ScheduleCalendarView: React.FC<ScheduleCalendarViewProps> = ({
  organizations,
  accessibleLocations,
  workerRelationships = [],
  selectedOrgId,
  canViewSchedule,
  canManageJobs,
  canManageApplications,
  canManageAssignments = false,
  canViewPayRate,
  canViewBillRate,
  onOpenJob,
  onOpenWorkerProfile,
}) => {
  const { t } = useI18n()
  const { isSimple, isAdvanced } = useAdminExperience()
  const industryLabels = useIndustryTerminology()
  const shiftNoun = industryLabels.explicitIndustry ? industryLabels.label('shift').toLowerCase() : 'shift'
  const shiftsNoun = industryLabels.explicitIndustry ? industryLabels.label('shifts').toLowerCase() : 'shifts'
  const workerNoun = industryLabels.explicitIndustry ? industryLabels.label('worker').toLowerCase() : 'worker'
  const workersNoun = industryLabels.explicitIndustry ? industryLabels.label('workers').toLowerCase() : 'workers'
  const jobNoun = industryLabels.explicitIndustry ? industryLabels.label('job').toLowerCase() : 'job/order'
  const jobsNoun = industryLabels.explicitIndustry ? industryLabels.label('jobs') : t('Jobs / Orders')
  const locationNoun = industryLabels.explicitIndustry ? industryLabels.label('location').toLowerCase() : 'location'
  const [mode, setMode] = useState<CalendarMode>(isSimple ? 'week' : 'month')
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))
  const [filters, setFilters] = useState({
    orgId: selectedOrgId || '',
    companyOrgId: '',
    agencyOrgId: '',
    locationId: '',
    jobId: '',
    status: '' as JobShiftStatus | '',
    fillState: '' as ScheduleFillState | '',
    conflictState: '' as ScheduleConflictState | '',
    availabilityState: '' as ScheduleAvailabilityState | '',
    skill: '',
  })
  const [events, setEvents] = useState<ScheduleCalendarEvent[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  const [selectedShiftId, setSelectedShiftId] = useState('')
  const [historyShiftId, setHistoryShiftId] = useState('')
  const [detailAssignments, setDetailAssignments] = useState<JobAssignment[]>([])
  const [detailApplications, setDetailApplications] = useState<ShiftApplication[]>([])
  const [detailMatches, setDetailMatches] = useState<JobShiftWorkerMatch[]>([])
  const [detailConflicts, setDetailConflicts] = useState<ScheduleShiftConflict[]>([])
  const [editLockReasons, setEditLockReasons] = useState<string[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [createForm, setCreateForm] = useState({ jobId: '', locationId: '', startsAt: '', endsAt: '', workersNeeded: 1, status: 'open' as JobShiftStatus, workMode: '' as WorkMode | '', payRate: '', billRate: '', currencyCode: 'CAD', notes: '', publishNow: true, repeatDaily: 1 })
  const [editForm, setEditForm] = useState({ startsAt: '', endsAt: '', locationId: '', workersNeeded: 1, status: 'open' as JobShiftStatus, workMode: '' as WorkMode | '', payRate: '', billRate: '', currencyCode: 'CAD', notes: '', visibility: '' })
  const [workerToAssign, setWorkerToAssign] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (isSimple && mode === 'month') setMode('week')
  }, [isSimple, mode])

  const range = useMemo(() => rangeFor(anchor, mode), [anchor, mode])
  const selectedEvent = events.find((event) => event.shiftId === selectedShiftId) || null
  const companyOrgs = organizations.filter((org) => org.org_type === 'company')
  const agencyOrgs = organizations.filter((org) => org.org_type === 'agency')

  const calendarFilters = useMemo<ScheduleCalendarFilters>(() => ({
    start: range.start.toISOString(),
    end: range.end.toISOString(),
    orgId: filters.orgId || undefined,
    companyOrgId: filters.companyOrgId || undefined,
    agencyOrgId: filters.agencyOrgId || undefined,
    locationId: filters.locationId || undefined,
    jobId: filters.jobId || undefined,
    status: filters.status || undefined,
    fillState: filters.fillState || undefined,
    conflictState: filters.conflictState || undefined,
  }), [
    filters.agencyOrgId,
    filters.companyOrgId,
    filters.conflictState,
    filters.fillState,
    filters.jobId,
    filters.locationId,
    filters.orgId,
    filters.status,
    range.end,
    range.start,
  ])

  const skillOptions = useMemo(() => Array.from(new Set(
    events.flatMap((event) => event.requiredSkills || []).concat(jobs.flatMap((job) => job.required_skill_tags || [])),
  )).filter(Boolean).sort(), [events, jobs])

  const visibleEvents = useMemo(() => events.filter((event) => {
    if (filters.skill && !(event.requiredSkills || []).includes(filters.skill)) return false
    if (filters.availabilityState === 'assigned') return event.assignedCount > 0
    if (filters.availabilityState === 'conflicted') return event.conflictCount > 0
    if (filters.availabilityState === 'unavailable') return event.conflictCount > 0 || event.warningCount > 0 || Boolean(event.hasMissingGeofenceWarning)
    if (filters.availabilityState === 'available') return event.assignedCount < event.requiredHeadcount && event.conflictCount === 0
    return true
  }), [events, filters.availabilityState, filters.skill])

  const eventsByDay = useMemo(() => visibleEvents.reduce<Record<string, ScheduleCalendarEvent[]>>((groups, event) => {
    const key = dateKey(new Date(event.start))
    groups[key] = [...(groups[key] || []), event]
    return groups
  }, {}), [visibleEvents])

  const scheduleSummary = useMemo(() => ({
    total: visibleEvents.length,
    unfilled: visibleEvents.filter((event) => openPositions(event.assignedCount, event.requiredHeadcount) > 0).length,
    atRisk: visibleEvents.filter((event) => event.conflictCount > 0 || event.warningCount > 0 || event.hasMissingGeofenceWarning).length,
    filled: visibleEvents.filter((event) => openPositions(event.assignedCount, event.requiredHeadcount) === 0).length,
  }), [visibleEvents])
  const activeFilterCount = [
    filters.orgId && filters.orgId !== selectedOrgId ? filters.orgId : '',
    filters.companyOrgId,
    filters.agencyOrgId,
    filters.locationId,
    filters.jobId,
    filters.status,
    filters.fillState,
    filters.conflictState,
    filters.availabilityState,
    filters.skill,
  ].filter(Boolean).length
  const clearFilters = () => setFilters({
    orgId: selectedOrgId || '',
    companyOrgId: '',
    agencyOrgId: '',
    locationId: '',
    jobId: '',
    status: '',
    fillState: '',
    conflictState: '',
    availabilityState: '',
    skill: '',
  })

  const candidateWorkers = useMemo(() => {
    const assignmentsByWorker = new Map(detailAssignments.flatMap((assignment) => {
      const workerProfileId = assignment.worker_profile_id
      return workerProfileId ? [[workerProfileId, assignment] as const] : []
    }))
    const conflictsByWorker = new Map<string, ScheduleShiftConflict[]>()
    detailConflicts.forEach((conflict) => {
      if (!conflict.worker_profile_id) return
      conflictsByWorker.set(conflict.worker_profile_id, [...(conflictsByWorker.get(conflict.worker_profile_id) || []), conflict])
    })
    const relationshipByWorker = new Map(workerRelationships.flatMap((worker) => {
      const workerProfileId = worker.worker_profile_id
      return workerProfileId ? [[workerProfileId, worker] as const] : []
    }))
    const ids = new Set([
      ...detailMatches.map((match) => match.worker_profile_id),
      ...detailAssignments.map((assignment) => assignment.worker_profile_id),
      ...detailConflicts.map((conflict) => conflict.worker_profile_id || ''),
      ...workerRelationships.map((worker) => worker.worker_profile_id),
    ].filter(isNonEmptyString))
    const rank = { available: 0, assigned: 1, conflicted: 2, unavailable: 3 }
    return Array.from(ids).map((workerProfileId) => {
      const match = detailMatches.find((row) => row.worker_profile_id === workerProfileId)
      const assignment = assignmentsByWorker.get(workerProfileId)
      const relationship = relationshipByWorker.get(workerProfileId)
      const conflicts = conflictsByWorker.get(workerProfileId) || []
      const blockingConflict = conflicts.find((conflict) => conflict.severity !== 'warning')
      const missingSkills = match?.missingRequiredSkills || match?.missing_skill_names || []
      const belowLevelSkills = matchBelowRequiredLevelSkills(match)
      const certificatePendingSkills = matchCertificatePendingSkills(match)
      const certificateExpiredSkills = matchCertificateExpiredSkills(match)
      const activeAssigned = assignment && activeAssignmentStatuses.includes(assignment.status)
      const unavailable = Boolean(blockingConflict || match?.eligible === false || match?.conflicts?.some((conflict) => conflict.severity !== 'warning'))
      const state: ScheduleAvailabilityState = activeAssigned ? 'assigned' : unavailable ? 'unavailable' : conflicts.length || Number(match?.conflict_count || 0) > 0 ? 'conflicted' : 'available'
      return {
        workerProfileId,
        label: match?.worker_display_name || assignment?.worker_display_name || relationship?.display_name || match?.worker_email || assignment?.worker_email || relationship?.worker_email || industryLabels.label('worker'),
        email: match?.worker_email || assignment?.worker_email || relationship?.worker_email || '',
        phone: match?.worker_phone || relationship?.worker_phone || '',
        state,
        match,
        assignment,
        conflicts,
        matchedSkills: match?.matched_skill_names || match?.skill_names || [],
        missingSkills,
        reliability: formatScore(match?.reliability_score),
        attendance: formatScore(match?.attendance_score),
        punctuality: formatScore(match?.punctuality_score),
        conflictMessage: missingSkills.length ? `Missing required skills: ${missingSkills.join(', ')}` : belowLevelSkills.length ? `Below required level: ${belowLevelSkills.join(', ')}` : certificatePendingSkills.length ? `Certificate pending: ${certificatePendingSkills.join(', ')}` : certificateExpiredSkills.length ? `Certificate expired: ${certificateExpiredSkills.join(', ')}` : conflictText(blockingConflict || conflicts[0]) || match?.match_reason || '',
        locationDiagnostic: matchDistanceDiagnostic(match),
      }
    }).filter((worker) => !filters.availabilityState || worker.state === filters.availabilityState)
      .sort((left, right) => rank[left.state] - rank[right.state] || left.label.localeCompare(right.label))
  }, [detailAssignments, detailConflicts, detailMatches, filters.availabilityState, industryLabels, workerRelationships])
  const selectedWorkerCandidate = candidateWorkers.find((worker) => worker.workerProfileId === workerToAssign)

  const loadCalendar = async () => {
    if (!canViewSchedule) return
    setLoading(true)
    setError('')
    try {
      const [eventRows, jobRows] = await Promise.all([listScheduleCalendarEvents(calendarFilters), listJobs()])
      setEvents(eventRows)
      setJobs(jobRows)
      setSelectedShiftId((current) => eventRows.some((event) => event.shiftId === current) ? current : '')
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load schedule calendar.')))
    } finally {
      setLoading(false)
    }
  }

  const loadDetail = async (shiftId: string) => {
    if (!shiftId) return
    try {
      const detail = await getScheduleShiftDetail(shiftId)
      setDetailAssignments(detail.assignments || [])
      setDetailApplications(detail.applications || [])
      setDetailMatches(detail.matches || [])
      setDetailConflicts(detail.conflicts || [])
      setEditLockReasons(detail.editLockReasons || [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load shift details.'))
    }
  }

  useEffect(() => {
    loadCalendar()
  }, [canViewSchedule, calendarFilters])

  useEffect(() => {
    if (!selectedShiftId) {
      setDetailAssignments([])
      setDetailApplications([])
      setDetailMatches([])
      setDetailConflicts([])
      setEditLockReasons([])
      setEditMode(false)
      return
    }
    loadDetail(selectedShiftId)
  }, [selectedShiftId])

  const move = (direction: -1 | 1) => setAnchor((current) => addDays(current, direction * (mode === 'month' ? 31 : mode === 'week' ? 7 : 1)))
  const runAction = async (action: () => Promise<void>, success: string) => {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await action()
      setMessage(success)
      await loadCalendar()
      if (selectedShiftId) await loadDetail(selectedShiftId)
    } catch (err) {
      setError(apiErrorMessage(err, t('Schedule action failed.')))
    } finally {
      setSaving(false)
    }
  }

  const toLocalInput = (value?: string | null, timezone?: string | null) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    if (!timezone) return date.toISOString().slice(0, 16)
    const values = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]))
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`
  }
  const toIso = (value: string) => value ? new Date(value).toISOString() : ''
  const timezoneForLocation = (locationId?: string | null) => accessibleLocations.find((location) => location.id === locationId)?.timezone || null
  const numberOrNull = (value: string) => value === '' ? null : Number(value)
  const openCreateForDay = (day = anchor) => {
    const start = new Date(day)
    start.setHours(9, 0, 0, 0)
    const end = new Date(day)
    end.setHours(17, 0, 0, 0)
    setCreateForm((current) => ({
      ...current,
      jobId: filters.jobId || current.jobId || jobs[0]?.id || '',
      locationId: filters.locationId || current.locationId,
      startsAt: toLocalInput(start.toISOString()),
      endsAt: toLocalInput(end.toISOString()),
      workMode: jobs.find((job) => job.id === (filters.jobId || current.jobId || jobs[0]?.id || ''))?.work_mode || current.workMode || '',
    }))
    setShowCreate(true)
  }
  const beginEdit = () => {
    if (!selectedEvent) return
    setEditForm({
      startsAt: toLocalInput(selectedEvent.start, selectedEvent.sourceTimezone),
      endsAt: toLocalInput(selectedEvent.end, selectedEvent.sourceTimezone),
      locationId: selectedEvent.locationId || '',
      workersNeeded: selectedEvent.requiredHeadcount,
      status: selectedEvent.status,
      workMode: selectedEvent.workMode || selectedEvent.work_mode || '',
      payRate: canViewPayRate && selectedEvent.payRate !== null && selectedEvent.payRate !== undefined ? String(selectedEvent.payRate) : '',
      billRate: canViewBillRate && selectedEvent.billRate !== null && selectedEvent.billRate !== undefined ? String(selectedEvent.billRate) : '',
      currencyCode: selectedEvent.currencyCode || 'CAD',
      notes: selectedEvent.notes || '',
      visibility: selectedEvent.visibility || '',
    })
    setEditMode(true)
  }
  const createShift = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!createForm.jobId) return
    await runAction(async () => {
      let createdShift: any = null
      const repeat = Math.max(1, Math.min(14, Number(createForm.repeatDaily) || 1))
      for (let index = 0; index < repeat; index += 1) {
        const start = new Date(toIso(createForm.startsAt))
        const end = new Date(toIso(createForm.endsAt))
        start.setDate(start.getDate() + index)
        end.setDate(end.getDate() + index)
        const localStart = new Date(`${createForm.startsAt}:00`)
        const localEnd = new Date(`${createForm.endsAt}:00`)
        localStart.setDate(localStart.getDate() + index)
        localEnd.setDate(localEnd.getDate() + index)
        const sourceTimezone = timezoneForLocation(createForm.locationId || jobs.find((job) => job.id === createForm.jobId)?.location_id)
        createdShift = await createJobShift(createForm.jobId, {
          locationId: createForm.locationId || null,
          startsAt: start.toISOString(),
          endsAt: end.toISOString(),
          ...(sourceTimezone ? {
            startsAtLocal: `${dateKey(localStart)}T${pad(localStart.getHours())}:${pad(localStart.getMinutes())}:00`,
            endsAtLocal: `${dateKey(localEnd)}T${pad(localEnd.getHours())}:${pad(localEnd.getMinutes())}:00`,
            sourceTimezone,
          } : {}),
          workersNeeded: createForm.workersNeeded,
          status: createForm.publishNow ? 'open' : createForm.status,
          workMode: createForm.workMode || null,
          ...(canViewPayRate ? { payRate: numberOrNull(createForm.payRate) } : {}),
          ...(canViewBillRate ? { billRate: numberOrNull(createForm.billRate) } : {}),
          ...((canViewPayRate || canViewBillRate) ? { currencyCode: createForm.currencyCode || 'CAD' } : {}),
          metadata: { notes: createForm.notes || undefined, source: 'schedule_calendar' },
        })
      }
      if (createdShift?.id) setSelectedShiftId(createdShift.id)
      setShowCreate(false)
    }, t('{resource} created.', { resource: industryLabels.label('shift') }))
  }
  const saveEdit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedEvent) return
    await runAction(async () => {
      const sourceTimezone = timezoneForLocation(editForm.locationId || selectedEvent.locationId) || selectedEvent.sourceTimezone || null
      await updateJobShift(selectedEvent.shiftId, {
        startsAt: toIso(editForm.startsAt),
        endsAt: toIso(editForm.endsAt),
        ...(sourceTimezone ? {
          startsAtLocal: `${editForm.startsAt}:00`,
          endsAtLocal: `${editForm.endsAt}:00`,
          sourceTimezone,
        } : {}),
        locationId: editForm.locationId || null,
        workersNeeded: editForm.workersNeeded,
        status: editForm.status,
        workMode: editForm.workMode || null,
        ...(canViewPayRate ? { payRate: numberOrNull(editForm.payRate) } : {}),
        ...(canViewBillRate ? { billRate: numberOrNull(editForm.billRate) } : {}),
        ...((canViewPayRate || canViewBillRate) ? { currencyCode: editForm.currencyCode || 'CAD' } : {}),
        metadata: { notes: editForm.notes || undefined, visibility: editForm.visibility || undefined, source: 'schedule_calendar_edit' },
      })
      setEditMode(false)
    }, t('{resource} updated.', { resource: industryLabels.label('shift') }))
  }
  const assignWorker = async (workerProfileId = workerToAssign) => {
    if (!selectedEvent || !workerProfileId) return
    const candidate = candidateWorkers.find((worker) => worker.workerProfileId === workerProfileId)
    if (candidate?.state === 'unavailable') {
      setError(candidate.conflictMessage || t('{worker} has blocking availability conflicts for this {shift}.', { worker: industryLabels.label('worker'), shift: shiftNoun }))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const assignment = await createJobAssignment({
        jobId: selectedEvent.jobId,
        jobShiftId: selectedEvent.shiftId,
        workerProfileId,
        assignedByOrgId: selectedOrgId || null,
        supplyingAgencyOrgId: selectedEvent.agencyOrgId || null,
        companyOrgId: selectedEvent.companyOrgId || null,
        status: 'confirmed',
      })
      const warnings = Array.isArray((assignment as any).warnings) ? (assignment as any).warnings : []
      setMessage(warnings.length
        ? t('{worker} assigned with warnings: {warnings}', { worker: industryLabels.label('worker'), warnings: warnings.map((warning: any) => warning.message || readable(warning.conflict_type || warning.conflictType)).join(' ') })
        : t('{worker} assigned.', { worker: industryLabels.label('worker') }))
      setWorkerToAssign('')
      await loadCalendar()
      if (selectedShiftId) await loadDetail(selectedShiftId)
    } catch (err) {
      setError(apiErrorMessage(err, t('{assignment} failed.', { assignment: industryLabels.label('assignment') })))
    } finally {
      setSaving(false)
    }
  }
  const updateAssignment = async (assignmentId: string, status: JobAssignmentStatus) => {
    await runAction(async () => {
      await updateJobAssignmentStatus(assignmentId, { status })
    }, t('{resource} updated.', { resource: industryLabels.label('assignment') }))
  }

  if (!canViewSchedule) return <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? t('Calendar access requires scheduling or {resources} view permission.', { resources: industryLabels.label('jobs').toLowerCase() }) : t('Calendar access requires scheduling or jobs view permission.')}</p>

  return (
    <div className="stack">
      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? t('{resources} calendar', { resources: industryLabels.label('shifts') }) : t('Scheduling calendar')}</h3>
          <p className="helper">{industryLabels.explicitIndustry ? t('Date-based view of {job} {shifts}, staffing fill, conflicts, warnings, and applicant load.', { job: jobNoun, shifts: shiftsNoun }) : t('Date-based view of job shifts, staffing fill, conflicts, warnings, and applicant load.')}</p>
        </div>
        <div className="inline-group">
          {(isSimple ? ['week', 'day'] : ['month', 'week', 'day'] as CalendarMode[]).map((item) => <button key={item} type="button" className={`admin-filter ${mode === item ? 'active' : ''}`} onClick={() => setMode(item as CalendarMode)}>{t(readable(item))}</button>)}
          <button type="button" className="btn btn-secondary" onClick={() => move(-1)}>{t('Previous')}</button>
          <button type="button" className="btn btn-secondary" onClick={() => setAnchor(startOfDay(new Date()))}>{t('Today')}</button>
          <button type="button" className="btn btn-secondary" onClick={() => move(1)}>{t('Next')}</button>
          <button type="button" className="btn btn-primary" disabled={!canManageJobs} onClick={() => openCreateForDay(anchor)}>{t('New {resource}', { resource: shiftNoun })}</button>
        </div>
      </div>

      <ErrorBanner message={error} />
      {message ? <p className="success-text">{message}</p> : null}

      <div className="admin-summary-grid schedule-summary-grid" aria-label={t('Schedule coverage summary')}>
        <div className="admin-summary-card"><span>{t('Visible shifts')}</span><strong>{scheduleSummary.total}</strong></div>
        <div className="admin-summary-card"><span>{t('Unfilled')}</span><strong>{scheduleSummary.unfilled}</strong></div>
        <div className="admin-summary-card"><span>{t('At risk')}</span><strong>{scheduleSummary.atRisk}</strong></div>
        <div className="admin-summary-card"><span>{t('Filled')}</span><strong>{scheduleSummary.filled}</strong></div>
      </div>

      <GuidedHelp>
        {(scheduleSummary.unfilled > 0 || scheduleSummary.atRisk > 0) ? (
          <div className="admin-card-grid" data-testid="schedule-next-actions">
            {scheduleSummary.unfilled > 0 ? <article className="admin-state admin-state--warning"><strong>{t('{count} shifts still need workers.', { count: scheduleSummary.unfilled })}</strong><p>{t('Open an unfilled shift to review eligible candidates and applications.')}</p></article> : null}
            {scheduleSummary.atRisk > 0 ? <article className="admin-state admin-state--warning"><strong>{t('{count} shifts need attention.', { count: scheduleSummary.atRisk })}</strong><p>{t('Open a shift with a conflict or warning to see the backend-reported blocker.')}</p></article> : null}
          </div>
        ) : null}
      </GuidedHelp>

      {isAdvanced ? <div className="panel workspace-panel admin-edit-panel stack">
        <div className="admin-section-header">
          <div>
            <strong>{t('Filters')}</strong>
            <p className="helper">{t('{count} active filters. Results stay in the authorized schedule scope.', { count: activeFilterCount })}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={clearFilters} disabled={activeFilterCount === 0}>{t('Clear filters')}</button>
        </div>
        <div className="admin-form-grid">
          <label>{t('Organization')}<select className="input" value={filters.orgId} onChange={(event) => setFilters({ ...filters, orgId: event.target.value })}><option value="">{t('All visible organizations')}</option>{organizations.map((org) => <option key={org.id} value={org.id}>{organizationLabel(organizations, org.id)}</option>)}</select></label>
          <label>{t('Company')}<select className="input" value={filters.companyOrgId} onChange={(event) => setFilters({ ...filters, companyOrgId: event.target.value })}><option value="">{t('All companies')}</option>{companyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
          <label>{t('Agency')}<select className="input" value={filters.agencyOrgId} onChange={(event) => setFilters({ ...filters, agencyOrgId: event.target.value })}><option value="">{t('All agencies')}</option>{agencyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
          <label>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}<select className="input" value={filters.locationId} onChange={(event) => setFilters({ ...filters, locationId: event.target.value })}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('locations').toLowerCase() }) : t('All locations')}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location, locationNoun)}</option>)}</select></label>
          <label>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job')}<select className="input" value={filters.jobId} onChange={(event) => setFilters({ ...filters, jobId: event.target.value })}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('jobs').toLowerCase() }) : t('All jobs')}</option>{jobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}</select></label>
          <label>{t('Status')}<select className="input" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value as JobShiftStatus | '' })}>{statuses.map((status) => <option key={status || 'all'} value={status}>{status ? t(readable(status)) : t('All statuses')}</option>)}</select></label>
          <label>{t('Fill')}<select className="input" value={filters.fillState} onChange={(event) => setFilters({ ...filters, fillState: event.target.value as ScheduleFillState | '' })}>{fillStates.map((state) => <option key={state || 'all'} value={state}>{state ? t(readable(state)) : t('All fill states')}</option>)}</select></label>
          <label>{t('Conflicts')}<select className="input" value={filters.conflictState} onChange={(event) => setFilters({ ...filters, conflictState: event.target.value as ScheduleConflictState | '' })}>{conflictStates.map((state) => <option key={state || 'all'} value={state}>{state ? t(readable(state)) : t('All conflict states')}</option>)}</select></label>
          <label>{t('Availability')}<select className="input" value={filters.availabilityState} onChange={(event) => setFilters({ ...filters, availabilityState: event.target.value as ScheduleAvailabilityState | '' })}>{availabilityStates.map((state) => <option key={state || 'all'} value={state}>{state ? t(readable(state)) : t('All availability states')}</option>)}</select></label>
          <label>{t('Skill / role')}<select className="input" value={filters.skill} onChange={(event) => setFilters({ ...filters, skill: event.target.value })}><option value="">{t('All skills')}</option>{skillOptions.map((skill) => <option key={skill} value={skill}>{skill}</option>)}</select></label>
        </div>
      </div> : (
        <details className="panel workspace-panel admin-edit-panel">
          <summary>{t('Filters')} · {t('{count} active', { count: activeFilterCount })}</summary>
          <div className="admin-form-grid" style={{ marginTop: '.75rem' }}>
            <label>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}<select className="input" value={filters.locationId} onChange={(event) => setFilters({ ...filters, locationId: event.target.value })}><option value="">{t('All visible worksites')}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location, locationNoun)}</option>)}</select></label>
            <label>{t('Attention')}<select className="input" value={filters.conflictState} onChange={(event) => setFilters({ ...filters, conflictState: event.target.value as ScheduleConflictState | '' })}><option value="">{t('All shifts')}</option><option value="conflict">{t('Conflicts')}</option><option value="warning">{t('Warnings')}</option><option value="clear">{t('No reported conflict')}</option></select></label>
            <button type="button" className="btn btn-secondary" onClick={clearFilters} disabled={activeFilterCount === 0}>{t('Clear filters')}</button>
          </div>
        </details>
      )}

      {isAdvanced ? <div className="schedule-calendar-legend">
          {colorStates.map((state) => <span key={state} className={`schedule-calendar-legend__item schedule-event--${state}`}><span />{t(readable(state))}</span>)}
      </div> : null}

      {loading ? <LoadingState label={t('Loading calendar...')} /> : null}
      {!loading && visibleEvents.length === 0 ? <EmptyState title={t('No {resources} in this calendar range', { resources: shiftsNoun })} description={industryLabels.explicitIndustry ? t('No {job} {shifts} match the selected dates and filters. Existing {jobs} scheduling remains available in Operations/List View.', { job: jobNoun, shifts: shiftsNoun, jobs: jobsNoun }) : t('No job shifts match the selected dates and filters. Existing Jobs / Orders scheduling remains available in Operations/List View.')} /> : null}

      {isAdvanced ? <div className={`schedule-calendar schedule-calendar--${mode}`}>
        {range.days.map((day) => {
          const key = dateKey(day)
          const dayEvents = eventsByDay[key] || []
          const outsideMonth = mode === 'month' && day.getMonth() !== anchor.getMonth()
          return (
            <section key={key} className={`schedule-calendar-day ${outsideMonth ? 'is-muted' : ''}`} onClick={() => openCreateForDay(day)}>
              <div className="schedule-calendar-day__header"><strong>{day.toLocaleDateString([], { weekday: mode === 'month' ? 'short' : 'long', month: 'short', day: 'numeric' })}</strong><span>{dayEvents.length}</span></div>
              <div className="schedule-calendar-day__events">
                {dayEvents.map((event) => (
                  <button key={event.shiftId} type="button" className={`schedule-event schedule-event--${event.colorState}`} onClick={(clickEvent) => { clickEvent.stopPropagation(); setSelectedShiftId(event.shiftId) }}>
                    <strong>{event.title}</strong>
                    <span>{formatTime(event.start, event.sourceTimezone)} - {formatTime(event.end, event.sourceTimezone)}</span>
                    <span>{t('{mode} · {place}', { mode: t(readable(event.workMode || event.work_mode || 'onsite')), place: (event.workMode || event.work_mode) === 'remote' ? t('Remote') : event.locationName || t('No {resource}', { resource: locationNoun }) })}</span>
                    <span>{t('{coverage}: {assigned}/{required} assigned · {open} open · {pending} pending', { coverage: t(coverageLabel(event.assignedCount, event.requiredHeadcount)), assigned: event.assignedCount, required: event.requiredHeadcount, open: openPositions(event.assignedCount, event.requiredHeadcount), pending: event.pendingApplicationCount })}</span>
                    {(event.conflictCount || event.warningCount || event.hasMissingGeofenceWarning) ? <span className="schedule-event__badges">{event.conflictCount ? t('{count} conflicts', { count: event.conflictCount }) : ''}{event.warningCount || event.hasMissingGeofenceWarning ? t('{count} warnings', { count: event.warningCount + (event.hasMissingGeofenceWarning ? 1 : 0) }) : ''}</span> : null}
                  </button>
                ))}
              </div>
            </section>
          )
        })}
      </div> : null}

      <section className="schedule-agenda" aria-label={t('Schedule agenda')}>
        <div className="admin-section-header">
          <h4>{t('Agenda')}</h4>
          <span className="helper">{t('Readable list view for the same filtered shifts.')}</span>
        </div>
        {visibleEvents.map((event) => (
          <button key={`agenda-${event.shiftId}`} type="button" className="schedule-agenda-row" onClick={() => setSelectedShiftId(event.shiftId)}>
            <span>
              <strong>{event.jobTitle || event.title}</strong>
              <span>{formatDateTime(event.start, event.sourceTimezone)} - {formatTime(event.end, event.sourceTimezone)}</span>
            </span>
            <span>{t('{mode} · {place}', { mode: t(readable(event.workMode || event.work_mode || 'onsite')), place: (event.workMode || event.work_mode) === 'remote' ? t('Remote') : event.locationName || t('No {resource}', { resource: locationNoun }) })}</span>
            <span>{event.sourceTimezone || t('Timezone provenance unavailable')}</span>
            <span>{t('{coverage}: {assigned}/{required} assigned, {open} open', { coverage: t(coverageLabel(event.assignedCount, event.requiredHeadcount)), assigned: event.assignedCount, required: event.requiredHeadcount, open: openPositions(event.assignedCount, event.requiredHeadcount) })}</span>
            <span className={`status-pill status-pill--${event.status}`}>{t(readable(event.status))}</span>
          </button>
        ))}
      </section>

      <DetailDrawer title={t('Create {resource}', { resource: shiftNoun })} isOpen={showCreate} onClose={() => setShowCreate(false)}>
        <form className="stack" onSubmit={createShift}>
          {jobs.length === 0 ? <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? t('No {resources} are available. Create the {resource} in {source} first.', { resources: industryLabels.label('jobs').toLowerCase(), resource: jobNoun, source: jobsNoun }) : t('No jobs/orders are available. Create the job/order in Jobs / Orders first.')}</p> : null}
          <div className="admin-form-grid">
            <label>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job / order')}<select className="input" value={createForm.jobId} onChange={(event) => {
              const job = jobs.find((row) => row.id === event.target.value)
              setCreateForm({ ...createForm, jobId: event.target.value, locationId: job?.location_id || createForm.locationId, workMode: job?.work_mode || createForm.workMode, payRate: canViewPayRate && job?.pay_rate != null ? String(job.pay_rate) : '', billRate: canViewBillRate && job?.bill_rate != null ? String(job.bill_rate) : '', currencyCode: (canViewPayRate || canViewBillRate) && job?.currency_code ? job.currency_code : createForm.currencyCode })
            }} disabled={!canManageJobs}><option value="">{industryLabels.explicitIndustry ? t('Choose {resource}', { resource: industryLabels.label('job').toLowerCase() }) : t('Choose job/order')}</option>{jobs.map((job) => <option key={job.id} value={job.id}>{job.title} / {organizationLabel(organizations, job.company_org_id || job.created_by_org_id)}</option>)}</select></label>
            <label>{t('Company / client')}<select className="input" value={jobs.find((job) => job.id === createForm.jobId)?.company_org_id || ''} disabled><option value="">{t('From {resource}', { resource: jobNoun })}</option>{organizations.filter((org) => org.org_type === 'company').map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label>{t('Managing agency')}<select className="input" value={jobs.find((job) => job.id === createForm.jobId)?.managing_agency_org_id || ''} disabled><option value="">{t('From {resource}', { resource: jobNoun })}</option>{organizations.filter((org) => org.org_type === 'agency').map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}</select></label>
            <label>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}<select className="input" value={createForm.locationId} onChange={(event) => setCreateForm({ ...createForm, locationId: event.target.value })} disabled={!canManageJobs}><option value="">{industryLabels.explicitIndustry ? t('Use {job} {location}', { job: jobNoun, location: locationNoun }) : t('Use job location')}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location, locationNoun)}</option>)}</select></label>
            <label>{t('Title / role')}<input className="input" value={jobs.find((job) => job.id === createForm.jobId)?.title || ''} disabled placeholder={t('From selected {resource}', { resource: jobNoun })} /></label>
            <label>{t('Start date/time')}<input type="datetime-local" className="input" value={createForm.startsAt} onChange={(event) => setCreateForm({ ...createForm, startsAt: event.target.value })} disabled={!canManageJobs} /></label>
            <label>{t('End date/time')}<input type="datetime-local" className="input" value={createForm.endsAt} onChange={(event) => setCreateForm({ ...createForm, endsAt: event.target.value })} disabled={!canManageJobs} /></label>
            <span className="helper admin-form-wide">{t('Schedule timezone')}: {timezoneForLocation(createForm.locationId || jobs.find((job) => job.id === createForm.jobId)?.location_id) || t('Device timezone fallback')}</span>
            <label>{t('Required headcount')}<input type="number" min={1} className="input" value={createForm.workersNeeded} onChange={(event) => setCreateForm({ ...createForm, workersNeeded: Number(event.target.value) })} disabled={!canManageJobs} /></label>
            <label>{t('Work mode')}<select className="input" value={createForm.workMode} onChange={(event) => setCreateForm({ ...createForm, workMode: event.target.value as WorkMode | '' })} disabled={!canManageJobs}>{workModes.map((mode) => <option key={mode || 'inherit'} value={mode}>{mode ? t(readable(mode)) : t('Inherit from job/order')}</option>)}</select></label>
            <label>{t('{resource} status', { resource: industryLabels.label('shift') })}<select className="input" value={createForm.status} onChange={(event) => setCreateForm({ ...createForm, status: event.target.value as JobShiftStatus })} disabled={!canManageJobs}>{statuses.filter(Boolean).map((status) => <option key={status} value={status}>{t(readable(status))}</option>)}</select></label>
            {canViewPayRate ? <label>{t('Pay rate')}<input type="number" min={0} step="0.01" className="input" value={createForm.payRate} onChange={(event) => setCreateForm({ ...createForm, payRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
            {canViewBillRate ? <label>{t('Bill rate')}<input type="number" min={0} step="0.01" className="input" value={createForm.billRate} onChange={(event) => setCreateForm({ ...createForm, billRate: event.target.value })} disabled={!canManageJobs} /></label> : null}
            {canViewPayRate || canViewBillRate ? <label>{t('Currency')}<input className="input" maxLength={3} value={createForm.currencyCode} onChange={(event) => setCreateForm({ ...createForm, currencyCode: event.target.value.toUpperCase() })} disabled={!canManageJobs} /></label> : null}
            <label>{t('Repeat daily')}<input type="number" min={1} max={14} className="input" value={createForm.repeatDaily} onChange={(event) => setCreateForm({ ...createForm, repeatDaily: Number(event.target.value) })} disabled={!canManageJobs} /></label>
            <label className="admin-check-row"><input type="checkbox" checked={createForm.publishNow} onChange={(event) => setCreateForm({ ...createForm, publishNow: event.target.checked })} disabled={!canManageJobs} /> {t('Publish/open now')}</label>
            <label className="admin-form-wide">{t('Notes')}<textarea className="input" value={createForm.notes} onChange={(event) => setCreateForm({ ...createForm, notes: event.target.value })} disabled={!canManageJobs} /></label>
          </div>
          <p className="helper">{industryLabels.explicitIndustry ? t('Required skills, credentials, visibility, and lightweight job creation follow the selected {job}. Use {jobs} for deeper setup.', { job: industryLabels.label('job').toLowerCase(), jobs: industryLabels.label('jobs') }) : t('Required skills, credentials, visibility, and lightweight job creation follow the selected job/order. Use Jobs / Orders for deeper setup.')}</p>
          <button type="submit" className="btn btn-primary" disabled={!canManageJobs || saving || !createForm.jobId || !createForm.startsAt || !createForm.endsAt}>{t('Create {resource}', { resource: shiftNoun })}</button>
        </form>
      </DetailDrawer>

      <DetailDrawer title={selectedEvent?.title || t('{resource} detail', { resource: industryLabels.label('shift') })} isOpen={Boolean(selectedEvent)} onClose={() => setSelectedShiftId('')}>
        {selectedEvent&&<UnionShiftAwards key={selectedEvent.shiftId} shiftId={selectedEvent.shiftId}/>}
        {selectedEvent ? (
          <div className="stack">
            <div className="admin-section-header">
              <div>
                <h3 style={{ margin: 0 }}>{selectedEvent.jobTitle || selectedEvent.title}</h3>
                <p className="helper">{formatDateTime(selectedEvent.start, selectedEvent.sourceTimezone)} - {formatDateTime(selectedEvent.end, selectedEvent.sourceTimezone)}</p>
                <p className="helper">{selectedEvent.sourceTimezone ? `${selectedEvent.sourceTimezone} (${selectedEvent.timezoneSource || 'location'})` : t('Legacy time — source timezone unknown')}</p>
              </div>
              <span className={`status-pill status-pill--${selectedEvent.status}`}>{t(readable(selectedEvent.status))}</span>
            </div>
            <div className="schedule-detail-grid">
              <span><strong>{t('Company')}</strong>{selectedEvent.companyName || '-'}</span>
              <span><strong>{t('Agency')}</strong>{selectedEvent.agencyName || '-'}</span>
              <span><strong>{t('Work mode')}</strong>{t(readable(selectedEvent.workMode || selectedEvent.work_mode || 'onsite'))}</span>
              <span><strong>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}</strong>{(selectedEvent.workMode || selectedEvent.work_mode) === 'remote' ? t('Remote - no onsite geofence required') : selectedEvent.locationName || '-'}</span>
              <span><strong>{t('Coverage')}</strong>{t('{state}: {assigned} assigned / {required} required / {open} open', { state: t(coverageLabel(selectedEvent.assignedCount, selectedEvent.requiredHeadcount)), assigned: selectedEvent.assignedCount, required: selectedEvent.requiredHeadcount, open: openPositions(selectedEvent.assignedCount, selectedEvent.requiredHeadcount) })}</span>
              <span><strong>{t('Applicants')}</strong>{selectedEvent.pendingApplicationCount}</span>
              <span><strong>{t('Conflicts')}</strong>{t('{conflicts} conflicts / {warnings} warnings', { conflicts: selectedEvent.conflictCount, warnings: selectedEvent.warningCount + (selectedEvent.hasMissingGeofenceWarning ? 1 : 0) })}</span>
            </div>
            <div className="admin-chip-list">{(selectedEvent.requiredSkills || []).map((skill) => <span key={skill} className="admin-chip">{skill}</span>)}{(selectedEvent.requiredSkills || []).length === 0 ? <span className="admin-chip">{t('No required skills')}</span> : null}</div>
            {editLockReasons.length ? <p className="admin-disabled-callout">{t('Editing is limited: {reasons}', { reasons: editLockReasons.join(' ') })}</p> : null}
            <div className="inline-group">
              <button type="button" className="btn btn-secondary" onClick={() => onOpenJob(selectedEvent.jobId)}>{t('Open full {resource}', { resource: jobNoun })}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setHistoryShiftId(selectedEvent.shiftId)}>{t('Operational history')}</button>
              <button type="button" className="btn btn-secondary" disabled={!canManageJobs || saving || editLockReasons.length > 0} onClick={beginEdit}>{t('Edit {resource}', { resource: shiftNoun })}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving || ['cancelled', 'completed'].includes(selectedEvent.status)} onClick={() => runAction(async () => { await publishJobShift(selectedEvent.shiftId) }, t('{resource} published/opened.', { resource: industryLabels.label('shift') }))}>{t('Publish/open {resource}', { resource: shiftNoun })}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving} onClick={() => runAction(async () => { await duplicateJobShift(selectedEvent.shiftId) }, t('{resource} duplicated.', { resource: industryLabels.label('shift') }))}>{t('Duplicate {resource}', { resource: shiftNoun })}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageJobs || saving || selectedEvent.status === 'cancelled'} onClick={() => runAction(async () => { await cancelJobShift(selectedEvent.shiftId) }, t('{resource} cancelled.', { resource: industryLabels.label('shift') }))}>{t('Cancel {resource}', { resource: shiftNoun })}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageApplications} onClick={() => onOpenJob(selectedEvent.jobId)}>{t('Review applicants')}</button>
            </div>
            {editMode ? (
              <form className="panel workspace-panel stack" onSubmit={saveEdit}>
                <h4 style={{ margin: 0 }}>{t('Edit {resource}', { resource: shiftNoun })}</h4>
                <div className="admin-form-grid">
                  <label>{t('Start date/time')}<input type="datetime-local" className="input" value={editForm.startsAt} onChange={(event) => setEditForm({ ...editForm, startsAt: event.target.value })} /></label>
                  <label>{t('End date/time')}<input type="datetime-local" className="input" value={editForm.endsAt} onChange={(event) => setEditForm({ ...editForm, endsAt: event.target.value })} /></label>
                  <span className="helper admin-form-wide">{t('Schedule timezone')}: {timezoneForLocation(editForm.locationId || selectedEvent.locationId) || selectedEvent.sourceTimezone || t('Device timezone fallback')}</span>
                  <label>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}<select className="input" value={editForm.locationId} onChange={(event) => setEditForm({ ...editForm, locationId: event.target.value })}><option value="">{t('Use {job} {location}', { job: jobNoun, location: locationNoun })}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location, locationNoun)}</option>)}</select></label>
                  <label>{t('Required headcount')}<input type="number" min={1} className="input" value={editForm.workersNeeded} onChange={(event) => setEditForm({ ...editForm, workersNeeded: Number(event.target.value) })} /></label>
                  <label>{t('Work mode')}<select className="input" value={editForm.workMode} onChange={(event) => setEditForm({ ...editForm, workMode: event.target.value as WorkMode | '' })}>{workModes.map((mode) => <option key={mode || 'inherit'} value={mode}>{mode ? t(readable(mode)) : t('Inherit from job/order')}</option>)}</select></label>
                  <label>{t('Status')}<select className="input" value={editForm.status} onChange={(event) => setEditForm({ ...editForm, status: event.target.value as JobShiftStatus })}>{statuses.filter(Boolean).map((status) => <option key={status} value={status}>{t(readable(status))}</option>)}</select></label>
                  {canViewPayRate ? <label>{t('Pay rate')}<input type="number" min={0} step="0.01" className="input" value={editForm.payRate} onChange={(event) => setEditForm({ ...editForm, payRate: event.target.value })} /></label> : null}
                  {canViewBillRate ? <label>{t('Bill rate')}<input type="number" min={0} step="0.01" className="input" value={editForm.billRate} onChange={(event) => setEditForm({ ...editForm, billRate: event.target.value })} /></label> : null}
                  {canViewPayRate || canViewBillRate ? <label>{t('Currency')}<input className="input" maxLength={3} value={editForm.currencyCode} onChange={(event) => setEditForm({ ...editForm, currencyCode: event.target.value.toUpperCase() })} /></label> : null}
                  <label>{t('Visibility')}<input className="input" value={editForm.visibility} onChange={(event) => setEditForm({ ...editForm, visibility: event.target.value })} placeholder={t('Inherited from {resource}', { resource: jobNoun })} /></label>
                  <label className="admin-form-wide">{t('Notes')}<textarea className="input" value={editForm.notes} onChange={(event) => setEditForm({ ...editForm, notes: event.target.value })} /></label>
                </div>
                <div className="inline-group">
                  <button type="submit" className="btn btn-primary" disabled={saving}>{t('Save {resource}', { resource: shiftNoun })}</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)}>{t('Cancel edit')}</button>
                </div>
              </form>
            ) : null}
            <section className="stack" aria-label={t('Shift applications')}>
              <div className="admin-section-header">
                <h4 style={{ margin: 0 }}>{t('Applications awaiting review')}</h4>
                <span className="helper">{t('{count} total', { count: detailApplications.length })}</span>
              </div>
              {detailApplications.length ? detailApplications.map((application) => {
                const blockingConflicts = (application.conflicts || []).filter((conflict) => conflict.severity !== 'warning')
                const warnings = (application.conflicts || []).filter((conflict) => conflict.severity === 'warning')
                const approvalDisabledReason = application.approval_disabled_reason
                  || blockingConflicts.map((conflict) => conflict.message).join(' ')
                const statusLabel = ['submitted', 'shortlisted'].includes(application.status)
                  ? t('Awaiting approval')
                  : t(readable(application.status))
                return (
                  <article key={application.id} className="panel workspace-panel stack">
                    <div className="admin-section-header">
                      <div>
                        <strong>{application.worker_display_name || application.worker_email || t('Worker applicant')}</strong>
                        <p className="helper">{application.applying_through_org_name || t('Direct application')} · {application.applied_at ? formatDateTime(application.applied_at) : t('Submitted')}</p>
                      </div>
                      <span className={`status-pill status-pill--${application.status}`}>{statusLabel}</span>
                    </div>
                    <div className="admin-chip-list">
                      {application.reliability_score != null ? <span className="admin-chip">{t('Reliability')}: {formatScore(application.reliability_score)}</span> : null}
                      {blockingConflicts.length ? <span className="admin-chip">{t('Hard conflicts')}: {blockingConflicts.length}</span> : <span className="admin-chip">{t('No hard assignment conflict')}</span>}
                      {warnings.length ? <span className="admin-chip">{t('Warnings')}: {warnings.length}</span> : null}
                    </div>
                    {(application.conflicts || []).map((conflict, index) => (
                      <p key={`${application.id}-conflict-${index}`} className={conflict.severity === 'warning' ? 'helper' : 'error-text'}>
                        {conflict.severity === 'warning' ? t('Warning') : t('Approval blocker')}: {conflict.message}
                      </p>
                    ))}
                    {approvalDisabledReason ? <p className="admin-disabled-callout">{t('Approval disabled: {reason}', { reason: approvalDisabledReason })}</p> : null}
                    <div className="inline-group">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!canManageAssignments || saving || !['submitted', 'shortlisted'].includes(application.status) || Boolean(approvalDisabledReason)}
                        title={approvalDisabledReason || undefined}
                        onClick={() => runAction(async () => { await approveShiftApplication(application.id) }, t('Application approved and assignment confirmed.'))}
                      >{t('Approve')}</button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!canManageApplications || saving || !['submitted', 'shortlisted'].includes(application.status)}
                        onClick={() => runAction(async () => { await rejectShiftApplication(application.id) }, t('Application rejected.'))}
                      >{t('Reject')}</button>
                      {onOpenWorkerProfile ? <button type="button" className="btn btn-secondary" onClick={() => onOpenWorkerProfile(application.worker_profile_id)}>{t('Open worker profile')}</button> : null}
                    </div>
                  </article>
                )
              }) : <EmptyState title={t('No applications yet')} description={t('Submitted worker requests will appear here immediately.')} />}
            </section>
            <section className="stack">
              <div className="admin-section-header">
                <h4 style={{ margin: 0 }}>{t('Assign {workers}', { workers: workersNoun })}</h4>
                <span className="helper">{t('{assigned} / {required} accepted or active', { assigned: detailAssignments.filter((assignment) => activeAssignmentStatuses.includes(assignment.status)).length, required: selectedEvent.requiredHeadcount })}</span>
              </div>
              {detailAssignments.length === 0 ? <p className="admin-disabled-callout">{t('No assigned {workers} yet.', { workers: workersNoun })}</p> : null}
              <ul className="admin-tree-list">
                {detailAssignments.map((assignment) => (
                  <li key={assignment.id} className="admin-list-row">
                    <span><strong>{assignment.worker_display_name || assignment.worker_email || (industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}</strong><span className="helper">{assignment.worker_email || assignment.supplying_agency_org_name || assignment.company_org_name || ''}</span></span>
                    <span className={`status-pill status-pill--${assignment.status}`}>{t(readable(assignment.status))}</span>
                    <button type="button" className="btn btn-ghost" disabled={!canManageAssignments || saving || assignment.status === 'confirmed'} onClick={() => updateAssignment(assignment.id, 'confirmed')}>{t('Confirm assignment')}</button>
                    <button type="button" className="btn btn-ghost" disabled={!canManageAssignments || saving || assignment.status === 'cancelled'} onClick={() => updateAssignment(assignment.id, 'cancelled')}>{t('Remove/cancel')}</button>
                    {assignment.worker_profile_id && onOpenWorkerProfile ? <button type="button" className="btn btn-ghost" onClick={() => onOpenWorkerProfile(assignment.worker_profile_id)}>{t('Open profile')}</button> : null}
                  </li>
                ))}
              </ul>
              <div className="admin-form-grid">
                <label>{t('Add {worker}', { worker: workerNoun })}<select className="input" value={workerToAssign} onChange={(event) => setWorkerToAssign(event.target.value)} disabled={!canManageAssignments || selectedEvent.assignedCount >= selectedEvent.requiredHeadcount}><option value="">{t('Select eligible {worker}', { worker: workerNoun })}</option>{workerRelationships.map((worker) => <option key={worker.worker_profile_id} value={worker.worker_profile_id}>{workerOptionLabel(worker, industryLabels.label('worker'))}</option>)}</select></label>
                <button type="button" className="btn btn-primary" disabled={!canManageAssignments || saving || !workerToAssign || selectedWorkerCandidate?.state === 'unavailable' || selectedEvent.assignedCount >= selectedEvent.requiredHeadcount} title={selectedWorkerCandidate?.state === 'unavailable' ? selectedWorkerCandidate.conflictMessage : undefined} onClick={() => assignWorker(workerToAssign)}>{t('Add {worker}', { worker: workerNoun })}</button>
              </div>
              {selectedWorkerCandidate?.state === 'unavailable' ? <p className="admin-disabled-callout">{t('Blocking conflict: {message}', { message: selectedWorkerCandidate.conflictMessage || '-' })}</p> : null}
              {selectedWorkerCandidate?.state === 'conflicted' ? <p className="helper">{t('Warning: {message}', { message: selectedWorkerCandidate.conflictMessage || t('Backend may return warnings during assignment.') })}</p> : null}
              {selectedEvent.assignedCount >= selectedEvent.requiredHeadcount ? <p className="helper">{t('No open slots remain. Overfill is disabled from Schedule.')}</p> : null}
              {workerRelationships.length === 0 ? <p className="admin-disabled-callout">{t('No eligible {workers} are visible in this tenant scope.', { workers: workersNoun })}</p> : null}
              <h4>{t('{worker} availability overlay', { worker: industryLabels.label('worker') })}</h4>
              <div className="schedule-worker-summary">
                {availabilityStates.filter(Boolean).map((state) => <span key={state} className={`schedule-badge schedule-badge--${state}`}>{t(readable(state))} {candidateWorkers.filter((worker) => worker.state === state).length}</span>)}
              </div>
              {candidateWorkers.length === 0 ? <p className="admin-disabled-callout">{t('No candidate availability data is visible for this {shift} and filter set.', { shift: shiftNoun })}</p> : null}
              <div className="schedule-worker-cards">
                {candidateWorkers.map((worker) => (
                  <article key={worker.workerProfileId} className={`schedule-worker-card schedule-worker-card--${worker.state}`}>
                    <div className="admin-section-header">
                      <div>
                        <strong>{worker.label}</strong>
                        <p className="helper">{worker.email || worker.phone || t('No contact')}{worker.match?.match_score != null ? ` - ${t('{score}% match', { score: worker.match.match_score })}` : ''}</p>
                      </div>
                      <span className={`schedule-badge schedule-badge--${worker.state}`}>{t(readable(worker.state))}</span>
                    </div>
                    <div className="admin-chip-list">
                      {worker.reliability ? <span className="admin-chip">{t('Reliability {value}', { value: worker.reliability })}</span> : null}
                      {worker.attendance ? <span className="admin-chip">{t('Attendance {value}', { value: worker.attendance })}</span> : null}
                      {worker.punctuality ? <span className="admin-chip">{t('Punctuality {value}', { value: worker.punctuality })}</span> : null}
                      {worker.matchedSkills.map((skill) => <span key={`matched-${worker.workerProfileId}-${skill}`} className="admin-chip">{t('Matched skill: {skill}', { skill })}</span>)}
                      {matchDiagnosticChips(worker.match, t).map((chip) => <span key={`${worker.workerProfileId}-${chip.key}`} className="admin-chip">{chip.label}</span>)}
                      {worker.assignment ? <span className="admin-chip">{t('Assignment {status}', { status: t(readable(worker.assignment.status)) })}</span> : null}
                    </div>
                    {worker.conflictMessage ? <p className="helper">{worker.conflictMessage}</p> : null}
                    {worker.locationDiagnostic ? <p className="helper">{worker.locationDiagnostic}</p> : null}
                    <div className="inline-group">
                      <button type="button" className="btn btn-primary" disabled={!canManageAssignments || saving || worker.state === 'assigned' || worker.state === 'unavailable' || selectedEvent.assignedCount >= selectedEvent.requiredHeadcount} onClick={() => assignWorker(worker.workerProfileId)}>{t('Assign')}</button>
                      {worker.workerProfileId && onOpenWorkerProfile ? <button type="button" className="btn btn-ghost" onClick={() => onOpenWorkerProfile(worker.workerProfileId)}>{t('Open profile')}</button> : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={t('Shift operational history')}
        description={t('Punches, breaks, execution segments, tracking sessions, and GPS trail for this scheduled shift.')}
        isOpen={Boolean(historyShiftId)}
        onClose={() => setHistoryShiftId('')}
        size="xl"
      >
        {historyShiftId ? <ShiftHistoryViewer jobShiftId={historyShiftId} /> : null}
      </FormModal>
    </div>
  )
}
